package com.sddamico.pages

import androidx.compose.runtime.*
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.apiGet
import com.sddamico.components.layouts.PageLayoutData
import com.sddamico.components.widgets.*
import com.sddamico.models.PlayerUniverseEntry
import com.sddamico.models.PlayerUniverseResponse
import com.sddamico.state.LocalAuthState
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import kotlin.math.max
import kotlin.math.min
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

private enum class SportTab(val label: String, val sportFilter: String?) {
    ALL("All", null),
    GOLF("Golf", "MENS_GOLF"),
    MENS_TENNIS("M Tennis", "MENS_TENNIS"),
    WOMENS_TENNIS("W Tennis", "WOMENS_TENNIS"),
}

private enum class TourFilter(val label: String) {
    ALL("All"),
    PGA("PGA"),
    LIV("LIV"),
}

private enum class OwnershipFilter(val label: String) {
    ALL("All"),
    OWNED("Owned"),
    FREE_AGENTS("Free Agents"),
}

private enum class SortOrder(val label: String) {
    POINTS("Points"),
    RANKING("Ranking"),
}

@InitRoute
fun initPlayers(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Players"))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun PlayersPage() {
    val ctx = rememberPageContext()
    val authState = LocalAuthState.current
    var universeData by remember { mutableStateOf<PlayerUniverseResponse?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var activeTab by remember { mutableStateOf(SportTab.ALL) }
    var tourFilter by remember { mutableStateOf(TourFilter.ALL) }
    var ownershipFilter by remember { mutableStateOf(OwnershipFilter.ALL) }
    var sortOrder by remember { mutableStateOf(SortOrder.POINTS) }
    var searchQuery by remember { mutableStateOf("") }
    var expandedPlayerId by remember { mutableStateOf<String?>(null) }
    var claimTarget by remember { mutableStateOf<PlayerUniverseEntry?>(null) }
    var refreshKey by remember { mutableStateOf(0) }

    // Virtualized-list bookkeeping. Only render cards near the viewport so
    // style/layout work scales with viewport size, not total player count.
    var scrollY by remember { mutableStateOf(0.0) }
    var viewportH by remember { mutableStateOf(800.0) }
    var viewportW by remember { mutableStateOf(1024.0) }
    var listTopOffset by remember { mutableStateOf(0.0) }
    val listContainerRef = remember { mutableStateOf<HTMLElement?>(null) }

    DisposableEffect(Unit) {
        scrollY = window.scrollY
        viewportH = window.innerHeight.toDouble()
        viewportW = window.innerWidth.toDouble()
        val onScroll: (Event) -> Unit = { scrollY = window.scrollY }
        val onResize: (Event) -> Unit = {
            viewportH = window.innerHeight.toDouble()
            viewportW = window.innerWidth.toDouble()
            listContainerRef.value?.let {
                listTopOffset = it.getBoundingClientRect().top + window.scrollY
            }
        }
        window.addEventListener("scroll", onScroll)
        window.addEventListener("resize", onResize)
        onDispose {
            window.removeEventListener("scroll", onScroll)
            window.removeEventListener("resize", onResize)
        }
    }

    // Re-measure list top whenever the filter UI above it might have changed height.
    LaunchedEffect(activeTab, tourFilter, ownershipFilter, sortOrder, searchQuery) {
        listContainerRef.value?.let {
            listTopOffset = it.getBoundingClientRect().top + window.scrollY
        }
    }

    // Close expanded cards when any filter changes
    LaunchedEffect(activeTab, tourFilter, ownershipFilter, sortOrder, searchQuery) {
        expandedPlayerId = null
    }

    LaunchedEffect(refreshKey) {
        try {
            universeData = apiGet(
                "/api/players/universe",
                PlayerUniverseResponse.serializer()
            )
        } catch (e: ApiException) {
            error = e.errorMessage
        } catch (e: Exception) {
            error = "Failed to load players"
        }
        loading = false
    }

    if (loading) {
        PageLoading()
        return
    }

    if (error != null) {
        ErrorCard(error!!)
        return
    }

    val data = universeData
    if (data == null) {
        EmptyState("No player data available")
        return
    }

    // Apply filters — memoized so we only re-run when inputs actually change.
    val filteredPlayers = remember(data, activeTab, tourFilter, ownershipFilter, sortOrder, searchQuery) {
        val needle = searchQuery.trim().lowercase()
        data.players
            .filter { player ->
                (activeTab.sportFilter == null || player.sport == activeTab.sportFilter) &&
                    (needle.isEmpty() || player.name.lowercase().contains(needle)) &&
                    when (ownershipFilter) {
                        OwnershipFilter.ALL -> true
                        OwnershipFilter.OWNED -> player.currentClub != null
                        OwnershipFilter.FREE_AGENTS -> player.currentClub == null
                    } &&
                    (activeTab != SportTab.GOLF || when (tourFilter) {
                        TourFilter.ALL -> true
                        TourFilter.PGA -> !player.isLiv
                        TourFilter.LIV -> player.isLiv
                    })
            }
            .let { players ->
                when (sortOrder) {
                    SortOrder.POINTS -> players.sortedByDescending { it.seasonPoints }
                    SortOrder.RANKING -> players.sortedWith(
                        compareBy<PlayerUniverseEntry> { it.rankingValue == null }
                            .thenBy { it.rankingValue }
                            .thenByDescending { it.seasonPoints }
                            .thenBy { it.id }
                    )
                }
            }
    }

    Column(Modifier.fillMaxWidth().gap(1.5.cssRem)) {
        // Header
        SpanText(
            "Players",
            Modifier
                .fontFamily(*HeadingFontFamily)
                .fontSize(2.cssRem)
                .fontWeight(FontWeight.Bold)
                .color(SitePalette.text)
        )

        // Desktop filters: single row
        Row(Modifier.gap(0.5.cssRem).fillMaxWidth().displayIfAtLeast(Breakpoint.MD), verticalAlignment = Alignment.CenterVertically) {
            SportTab.entries.forEach { tab ->
                FilterPill(tab.label, tab == activeTab) { activeTab = tab; if (tab != SportTab.GOLF) tourFilter = TourFilter.ALL }
            }
            if (activeTab == SportTab.GOLF) {
                FilterDivider()
                TourFilter.entries.forEach { filter ->
                    FilterPill(filter.label, filter == tourFilter) { tourFilter = filter }
                }
            }
            FilterDivider()
            OwnershipFilter.entries.forEach { filter ->
                FilterPill(filter.label, filter == ownershipFilter) { ownershipFilter = filter }
            }
            FilterDivider()
            SortOrder.entries.forEach { sort ->
                FilterPill(sort.label, sort == sortOrder) { sortOrder = sort }
            }
            FilterDivider()
            SearchInput(searchQuery) { searchQuery = it }
        }

        // Mobile filters: stacked rows
        Column(Modifier.fillMaxWidth().gap(0.5.cssRem).displayUntil(Breakpoint.MD)) {
            Row(Modifier.gap(0.5.cssRem).styleModifier { property("flex-wrap", "wrap") }) {
                SportTab.entries.forEach { tab ->
                    FilterPill(tab.label, tab == activeTab) { activeTab = tab; if (tab != SportTab.GOLF) tourFilter = TourFilter.ALL }
                }
            }
            if (activeTab == SportTab.GOLF) {
                Row(Modifier.gap(0.5.cssRem).styleModifier { property("flex-wrap", "wrap") }) {
                    TourFilter.entries.forEach { filter ->
                        FilterPill(filter.label, filter == tourFilter) { tourFilter = filter }
                    }
                }
            }
            Row(Modifier.gap(0.5.cssRem).styleModifier { property("flex-wrap", "wrap") }) {
                OwnershipFilter.entries.forEach { filter ->
                    FilterPill(filter.label, filter == ownershipFilter) { ownershipFilter = filter }
                }
            }
            Row(Modifier.gap(0.5.cssRem).styleModifier { property("flex-wrap", "wrap") }) {
                SortOrder.entries.forEach { sort ->
                    FilterPill(sort.label, sort == sortOrder) { sortOrder = sort }
                }
            }
            SearchInput(searchQuery, fillWidth = true) { searchQuery = it }
        }

        // Results count
        SpanText(
            "${filteredPlayers.size} players",
            Modifier.fontSize(0.8125.cssRem).color(SitePalette.textMuted)
        )

        // Player list
        if (filteredPlayers.isEmpty()) {
            EmptyState("No players match your filters")
        } else {
            // Column headers — aligned to card inner layout (desktop only)
            Row(
                Modifier
                    .fillMaxWidth()
                    .padding(leftRight = (0.875 + 0.1875).cssRem) // card padding + half of 3px left border
                    .margin(bottom = (-0.5).cssRem)
                    .displayIfAtLeast(Breakpoint.MD),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    "Player",
                    Modifier
                        .fontSize(0.6875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .color(SitePalette.textMuted)
                        .styleModifier { property("min-width", "12rem"); property("text-transform", "uppercase"); property("letter-spacing", "0.05em") }
                )
                SpanText(
                    "Sport",
                    Modifier
                        .fontSize(0.6875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .color(SitePalette.textMuted)
                        .styleModifier { property("text-transform", "uppercase"); property("letter-spacing", "0.05em") }
                )
                SpanText(
                    "Ranking",
                    Modifier
                        .fontSize(0.6875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .color(SitePalette.textMuted)
                        .margin(left = 0.625.cssRem)
                        .styleModifier { property("text-transform", "uppercase"); property("letter-spacing", "0.05em") }
                )
                Spacer()
                SpanText(
                    "Club",
                    Modifier
                        .fontSize(0.6875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .color(SitePalette.textMuted)
                        .styleModifier { property("min-width", "7rem"); property("text-align", "right"); property("text-transform", "uppercase"); property("letter-spacing", "0.05em") }
                )
                SpanText(
                    "Points",
                    Modifier
                        .fontSize(0.6875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .color(SitePalette.textMuted)
                        .margin(left = 1.cssRem)
                        .styleModifier { property("min-width", "5rem"); property("text-align", "right"); property("text-transform", "uppercase"); property("letter-spacing", "0.05em") }
                )
                // Spacer for chevron column
                SpanText(
                    "",
                    Modifier.margin(left = 0.75.cssRem).styleModifier { property("min-width", "0.5625rem") }
                )
            }

            // Virtualized list: only render cards near the viewport. Compose-HTML
            // has no LazyColumn, and rendering all ~800 cards puts >25k DOM nodes
            // on the page, which makes every sort/filter pass trigger a multi-second
            // browser layout. Spacers above/below preserve total scroll height.
            //
            // rowHeightPx MUST include the 12px margin-bottom on each card wrapper
            // below — otherwise spacer height ≠ removed content height, and scroll
            // anchoring on the page kicks off a runaway scroll feedback loop as
            // rows mount/unmount during scroll.
            val rowHeightPx = if (viewportW < 768.0) 92.0 else 68.5
            val overscan = 8
            val effectiveScroll = (scrollY - listTopOffset).coerceAtLeast(0.0)
            val windowFirst = max(
                0,
                (effectiveScroll / rowHeightPx).toInt() - overscan,
            )
            val windowLast = min(
                filteredPlayers.size - 1,
                ((effectiveScroll + viewportH) / rowHeightPx).toInt() + overscan,
            )
            val topSpacerH = (windowFirst * rowHeightPx).coerceAtLeast(0.0)
            val bottomSpacerH =
                ((filteredPlayers.size - 1 - windowLast) * rowHeightPx).coerceAtLeast(0.0)

            Div(
                attrs = Modifier.fillMaxWidth().toAttrs {
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        // Disable scroll anchoring. As items unmount above the viewport,
                        // any mismatch between spacer height and actual removed height
                        // would otherwise prompt the browser to "correct" scrollY,
                        // which fires another scroll event and runs away.
                        property("overflow-anchor", "none")
                    }
                    ref { element ->
                        listContainerRef.value = element
                        listTopOffset = element.getBoundingClientRect().top + window.scrollY
                        onDispose { listContainerRef.value = null }
                    }
                }
            ) {
                if (topSpacerH > 0) {
                    Div(attrs = {
                        style {
                            property("height", "${topSpacerH}px")
                            property("flex-shrink", "0")
                        }
                    })
                }
                val canClaim = authState.user != null && authState.user.clubId != null && !authState.user.isImpersonating
                val userClubId = authState.user?.clubId
                if (windowFirst <= windowLast) {
                    for (i in windowFirst..windowLast) {
                        val player = filteredPlayers[i]
                        // Wrap in a fixed-margin div so we get a consistent 12px row
                        // gap without relying on `gap:` (which would also be applied
                        // between spacers and break the windowed height math).
                        Div(attrs = {
                            style {
                                property("margin-bottom", "12px")
                                property("flex-shrink", "0")
                            }
                        }) {
                            val isOtherClub = player.currentClub != null && userClubId != null && player.currentClub.id != userClubId
                            val onToggleExpand = remember(player.id) {
                                {
                                    expandedPlayerId = if (expandedPlayerId == player.id) null else player.id
                                }
                            }
                            val onClaim: ((PlayerUniverseEntry) -> Unit)? = remember(canClaim) {
                                if (canClaim) { p -> claimTarget = p } else null
                            }
                            val onTrade: (() -> Unit)? = remember(isOtherClub, authState.user?.isImpersonating, player.currentClub?.id, player.id) {
                                if (isOtherClub && authState.user?.isImpersonating == false) {
                                    {
                                        ctx.router.tryRoutingTo(
                                            "/trade?receiverClubId=${player.currentClub!!.id}&playerId=${player.id}"
                                        )
                                    }
                                } else null
                            }
                            UniversePlayerCard(
                                player,
                                data.leagueYearId,
                                isExpanded = expandedPlayerId == player.id,
                                onToggleExpand = onToggleExpand,
                                onClaim = onClaim,
                                onTrade = onTrade,
                            )
                        }
                    }
                }
                if (bottomSpacerH > 0) {
                    Div(attrs = {
                        style {
                            property("height", "${bottomSpacerH}px")
                            property("flex-shrink", "0")
                        }
                    })
                }
            }
        }
    }

    // Claim modal
    val target = claimTarget
    if (target != null && authState.user?.clubId != null) {
        ClaimModal(
            player = target,
            clubId = authState.user.clubId,
            isWaiverClaim = target.waiverEndDate != null,
            onClose = { claimTarget = null },
            onSuccess = { claimTarget = null; refreshKey++ },
        )
    }
}

@Composable
private fun FilterDivider() {
    Div(
        Modifier
            .styleModifier { property("width", "1px"); property("align-self", "stretch") }
            .backgroundColor(SitePalette.border)
            .margin(leftRight = 0.25.cssRem)
            .toAttrs()
    ) {}
}

@Composable
private fun SearchInput(value: String, fillWidth: Boolean = false, onValueChange: (String) -> Unit) {
    Input(InputType.Text) {
        value(value)
        onInput { onValueChange(it.value) }
        attr("placeholder", "Search players...")
        style {
            if (fillWidth) {
                property("flex", "1")
                property("width", "100%")
            } else {
                property("flex", "1")
                property("max-width", "12rem")
            }
            property("min-width", "6rem")
            property("box-sizing", "border-box")
            padding(0.375.cssRem, 0.75.cssRem)
            fontSize(0.8125.cssRem)
            borderRadius(9999.px)
            border(1.px, LineStyle.Solid, SitePalette.border)
            backgroundColor(SitePalette.surface)
            color(SitePalette.text)
            outline("none")
        }
    }
}
